/**
 * src/features/referral/ui/RefereeCallout.tsx
 *
 * Inline non-blocking callout banner shown on the signup screen when the
 * user arrived via a referral link (sessionStorage 'referral_code' present).
 *
 * Design constraints (RESEARCH Pitfall 6, REFUI-03):
 *  - MUST NOT use a modal or overlay — inline motion.div banner only
 *  - Inserted between the <p> label and the interactive blocks in LoginCard.tsx
 *    (Phase 42 — moved from AuthFormCard.tsx, which is now a thin shell)
 *  - Dismiss state is LOCAL (component state only) — callout reappears
 *    on page reload with same sessionStorage code, which is correct UX
 *    (the code survives reload; user hasn't been prompted not to show it)
 *
 * T-27-04: callout_heading/body rendered as React text content (auto-escaped).
 */

import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';

export const RefereeCallout: FC = () => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [dismissed, setDismissed] = useState(false);

  // Read sessionStorage — renders null when no referral code present
  const code = sessionStorage.getItem('referral_code');

  // Only bail out when there is no code at all; keep rendering when dismissed
  // so AnimatePresence can observe the element leaving and play the exit animation.
  if (!code) {
    return null;
  }

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          role="note"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: reduced ? 0 : 0.2 }}
          className="bg-brand-50 dark:bg-brand-900/20 border-l-4 border-brand-600 rounded-r-lg px-4 py-3 flex items-start justify-between gap-2"
        >
          <div>
            <p className="text-body font-bold text-slate-800 dark:text-slate-100">
              {t('referral.callout_heading', 'Your first month of Pro is free')}
            </p>
            <p className="text-body text-slate-600 dark:text-slate-400">
              {t('referral.callout_body', 'Sign up to claim your referral bonus.')}
            </p>
          </div>
          <button
            onClick={() => setDismissed(true)}
            aria-label={t('referral.callout_dismiss_aria', 'Dismiss referral bonus callout')}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors shrink-0"
          >
            <X size={14} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
